// @ts-nocheck

import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as S from './styles';

// Actions
import UserItemActions from 'app/shared/flux/actions/UserItemActions';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import { expandSearch } from 'app/shared/flux/actions/HomeHubActions';

// Components
import Head from 'react-helmet';
import UserItemsTitleBar from 'app/shared/modules/user-items/UserItemsTitleBar';
import HubCollection from './HubCollection/HubCollection';
import Center from 'app/shared/modules/Center';
import Button from 'app/shared/core/Button';
import Row from 'app/shared/core/Row';
import NearbyAreas from './NearbyAreas/NearbyAreas';

// Misc / Util
import constants from 'app/shared/constants/ConstantsBundle';
import pathsManifest from 'app/shared/utils/pathsManifest';
import { isEmpty } from 'lodash';
import { emitTrackViewMyHubEvent } from 'app/shared/models/Clickstream/PageViewEvents';

const MyHubPage = () => {
  const dispatch = useDispatch();
  const isMobile = useSelector((state) => state.app.device.screenWidth === 'sm');
  const userLoggedIn = useSelector((state) => state.user.loggedIn);
  const recentSearch = useSelector((state) => state.user.search.recent);

  const recentlySearched = useSelector((state) => state.listings.listingGroups.byCoords);
  const inquired = useSelector((state) => state.listings.listingGroups.inquired);
  const favorite = useSelector((state) => state.listings.listingGroups.favorite);
  const viewed = useSelector((state) => state.listings.listingGroups.viewed);

  const areaName = useSelector((state) => state.homehub.expandMySearchName);
  const nearbyAreas = useSelector((state) => state.homehub.expandMySearch);

  const [recentlySearchedLoading, setRecentlySearchedLoading] = useState(true);
  const [inquiryLoading, setInquiryLoading] = useState(true);
  const [favoriteLoading, setFavoriteLoading] = useState(true);
  const [viewedLoading, setViewedLoading] = useState(true);
  const [nearbyAreasLoading, setNearbyAreasLoading] = useState(true);

  const [recentlySearchedError, setRecentlySearchedError] = useState(false);
  const [inquiryError, setInquiryError] = useState(false);
  const [favoriteError, setFavoriteError] = useState(false);
  const [viewedError, setViewedError] = useState(false);
  const [nearbyAreasError, setNearbyAreasError] = useState(false);

  const handleLoginRedirect = useCallback(() => {
    window.router.transitionTo(pathsManifest.LoginPage + '?redirect=' + pathsManifest.MyHubPage);
  }, []);

  // fetch listing groups and nearby areas
  useEffect(() => {
    if (userLoggedIn) {
      emitTrackViewMyHubEvent();
      dispatch(
        ListingEngineActions.fetchListingsByCoords({
          offset: 0,
          limit: 4,
          filter: recentSearch.filter,
          area: recentSearch.areaInfo,
        }),
      )
        .catch(() => setRecentlySearchedError(true))
        .finally(() => setRecentlySearchedLoading(false));

      dispatch(UserItemActions.getUserItems(constants.FAVORITE, 0, 12, '', true))
        .catch(() => setFavoriteError(true))
        .finally(() => setFavoriteLoading(false));

      dispatch(UserItemActions.getUserItems(constants.INQUIRY, 0, 6, '', true))
        .catch(() => setInquiryError(true))
        .finally(() => setInquiryLoading(false));

      dispatch(UserItemActions.getUserItems(constants.VIEWED, 0, 4, '', true))
        .catch(() => setViewedError(true))
        .finally(() => setViewedLoading(false));

      if (!recentSearch.areaInfo) {
        setNearbyAreasError(true);
        setNearbyAreasLoading(false);
        return;
      }

      dispatch(
        expandSearch({
          recentArea: recentSearch.areaInfo,
          userLocation: {},
          limit: 6,
        }),
      )
        .catch(() => setNearbyAreasError(true))
        .finally(() => setNearbyAreasLoading(false));
    }
  }, [recentSearch.filter, recentSearch.areaInfo, dispatch, userLoggedIn]);

  const handleUserItemOffMarketToggle = useCallback(
    (listingType, numListings, setLoadingFunc, setErrorFunc) => {
      return (showOffMarketProperties) => {
        setLoadingFunc(true);
        setErrorFunc(false);

        dispatch(UserItemActions.getUserItems(listingType, 0, numListings, '', !showOffMarketProperties))
          .catch(() => setErrorFunc(true))
          .finally(() => setLoadingFunc(false));
      };
    },
    [dispatch],
  );

  return (
    <S.MyHubPage>
      <Head title={`My Hub - HotPads`} />
      <UserItemsTitleBar>My Hub</UserItemsTitleBar>

      {!userLoggedIn && (
        <Center>
          <S.PromptLoginTitle>
            We're excited for you to easily manage the properties that interest you!
          </S.PromptLoginTitle>
          <S.PromptLoginDescription>
            Sign in or register to see the properties you've interacted with like last searched, contacted, viewed, and
            more on any device you use.
          </S.PromptLoginDescription>
          <Row>
            <Button onClick={handleLoginRedirect}>Sign in / Register</Button>
          </Row>
        </Center>
      )}

      {userLoggedIn && (
        <S.MainContent>
          <HubCollection
            title={'Act on Your Favorites'}
            ariaLabelVerb={'favorited'}
            listings={favorite}
            displayCount={isMobile ? 3 : 6}
            showOffMarketToggle
            handleOffMarketToggle={handleUserItemOffMarketToggle(
              constants.FAVORITE,
              12,
              setFavoriteLoading,
              setFavoriteError,
            )}
            ctaMessage={'View all Favorites →'}
            ctaLink={`${pathsManifest.MyListPage}?index=1`}
            noListingsMessage={`You haven't favorited any rentals. When you do, they'll appear here.`}
            isLoading={favoriteLoading}
            error={favoriteError}
            analyticsLabel={'favorite'}
            triggerObject={'my_hub_component|favorite'}
          />
          <HubCollection
            title={'Contacted Rentals'}
            ariaLabelVerb={'contacted'}
            listings={inquired}
            displayCount={isMobile ? 3 : 6}
            showOffMarketToggle
            handleOffMarketToggle={handleUserItemOffMarketToggle(
              constants.INQUIRY,
              6,
              setInquiryLoading,
              setInquiryError,
            )}
            ctaMessage={'View all Contacts →'}
            ctaLink={`${pathsManifest.MyListPage}?index=2`}
            noListingsMessage={`You haven't contacted any rentals. When you do, they'll appear here.`}
            isLoading={inquiryLoading}
            error={inquiryError}
            analyticsLabel={'contacted'}
            triggerObject={'my_hub_component|contact'}
          />
          {/* TODO: HP-5938 - uncomment this collection when tours param is added to /api/v2/user/item/listV2 and load content into it */}
          {/* TODO: HP-5938 - link to tours catalog page when created and update handleOffMarketToggle constant */}
          {/* <HubCollection
                        title={'Prepare for your Upcoming Tours'}
                        ariaLabelVerb={'recently scheduled tours with'}
                        listings={scheduledTour}
                        displayCount={isMobile ? 3 : 6}
                        ctaMessage={'View all Tours →'}
                        ctaLink={``}
                        noListingsMessage={`You haven't scheduled any tours recently. When you do, they'll appear here.`}
                        isLoading={scheduledTourLoading}
                        error={scheduledTourError}
                        analyticsLabel={'tour'}
                    /> */}
          <HubCollection
            title={'Pick Up Where You Left Off'}
            ariaLabelVerb={'viewed'}
            listings={viewed}
            displayCount={isMobile ? 2 : 4}
            showOffMarketToggle
            handleOffMarketToggle={handleUserItemOffMarketToggle(constants.VIEWED, 4, setViewedLoading, setViewedError)}
            ctaMessage={'View your last viewed homes →'}
            ctaLink={pathsManifest.ViewedItemsPage}
            noListingsMessage={`You haven't viewed any rentals. When you do, they'll appear here.`}
            isLoading={viewedLoading}
            error={viewedError}
            analyticsLabel={'viewed'}
            triggerObject={'my_hub_component|last_viewed'}
          />
          <HubCollection
            title={'Based on Your Last Search'}
            ariaLabelVerb={'recently searched for'}
            listings={recentlySearched}
            displayCount={isMobile ? 2 : 4}
            ctaMessage={isEmpty(recentSearch) ? '' : `View more apartments ${recentSearch.locationString} →`}
            ctaLink={isEmpty(recentSearch) ? '' : recentSearch.url}
            noListingsMessage={
              isEmpty(recentSearch)
                ? `You haven't searched for any rentals or your most recent search doesn't apply to any current listings. When you do, properties in your most recent search will appear here.`
                : `Your most recent search doesn't apply to any rentals, try adjusting your filters.`
            }
            isLoading={recentlySearchedLoading}
            error={recentlySearchedError}
            analyticsLabel={'recentSearch'}
            triggerObject={'my_hub_component|last_search'}
          />
          {(nearbyAreasLoading || !isEmpty(nearbyAreas)) && !nearbyAreasError && (
            <NearbyAreas areaName={areaName} nearbyAreas={nearbyAreas} isLoading={nearbyAreasLoading} />
          )}
        </S.MainContent>
      )}
    </S.MyHubPage>
  );
};

export default MyHubPage;
